import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData, saveDossier } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import SaveLoader from "../shared/SaveLoader";
import ConfidenceLabel from "../shared/ConfidenceLabel";
import useConfidence from "../../hooks/useConfidence";
import "./InfosGenerales.css";
import toast from "react-hot-toast";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";

const RUBRIQUE = "infos_generales";

const TABS = [
  { id: "malade",      label: "Infos du Malade"   },
  { id: "parents",     label: "Infos des Parents"  },
  { id: "point_focal", label: "Point Focal"        },
  { id: "autres",      label: "Autres Contacts"    },
];

const INITIAL = {
  malade: {
    date_naissance: "", age_ans: "", age_mois: "", lieu_naissance: "",
    sexe: "", race: "", nationalite: "", scolarise: "", niveau_etude: "",
    ville: "", commune: "", village: "", campement: "", quartier: "",
    zone_habitation: "", hebergement_abidjan: "",
  },
  parents: {
    famille: "",
    type_mariage: "",
    famille_monoparentale_avec: "",
    nombre_femmes: "",
    pere_nom: "", pere_age: "", pere_ethnie: "", pere_niveau_etude: "",
    pere_religion: "", pere_religion_autre: "", pere_profession: "",
    pere_profession_detail: "", pere_autres_contacts: "",
    mere_nom: "", mere_age: "", mere_ethnie: "", mere_niveau_etude: "",
    mere_religion: "", mere_religion_autre: "", mere_profession: "",
    mere_profession_detail: "", mere_autres_contacts: "",
  },
  point_focal: { nom: "", lien_parente: "", profession: "", lieu_habitation: "", email: "", mobile: "" },
  autres:      { nom: "", lien_parente: "", contacts: "" },
};

const InfosGenerales = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const [saving, setSaving] = useState(false);

  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;

  const savedMalade     = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.malade      || {});
  const savedParents    = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.parents     || {});
  const savedPointFocal = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.point_focal || {});
  const savedAutres     = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.autres      || {});

  const [malade,     setMalade]     = useState({ ...INITIAL.malade,      ...savedMalade });
  const [parents,    setParents]    = useState({ ...INITIAL.parents,     ...savedParents });
  const [pointFocal, setPointFocal] = useState({ ...INITIAL.point_focal, ...savedPointFocal });
  const [autres,     setAutres]     = useState({ ...INITIAL.autres,      ...savedAutres });

  const maladeRef     = useRef(malade);
  const parentsRef    = useRef(parents);
  const pointFocalRef = useRef(pointFocal);
  const autresRef     = useRef(autres);

  useEffect(() => { maladeRef.current     = malade;     }, [malade]);
  useEffect(() => { parentsRef.current    = parents;    }, [parents]);
  useEffect(() => { pointFocalRef.current = pointFocal; }, [pointFocal]);
  useEffect(() => { autresRef.current     = autres;     }, [autres]);

  useEffect(() => {
    setMalade({ ...INITIAL.malade, ...savedMalade });
    setParents({ ...INITIAL.parents, ...savedParents });
    setPointFocal({ ...INITIAL.point_focal, ...savedPointFocal });
    setAutres({ ...INITIAL.autres, ...savedAutres });
  }, [id]);

  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [imgIndex,  setImgIndex]  = useState(0);

// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
  // ── Confidence ───────────────────────────────────────────────────────────────
  const { getConfidence } = useConfidence(id, RUBRIQUE);
  const gm  = (field) => getConfidence("malade",      field);
  const gp  = (field) => getConfidence("parents",     field);
  const gpf = (field) => getConfidence("point_focal", field);
  const ga  = (field) => getConfidence("autres",      field);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const change = (setter) => (field, value) => setter((prev) => ({ ...prev, [field]: value }));
  const changeMalade     = change(setMalade);
  const changeParents    = change(setParents);
  const changePointFocal = change(setPointFocal);
  const changeAutres     = change(setAutres);

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "malade",      maladeRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "parents",     parentsRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "point_focal", pointFocalRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "autres",      autresRef.current));
  };

  useEffect(() => {
    return () => { handleSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, "malade",      malade));
    dispatch(updateFormData(id, RUBRIQUE, "parents",     parents));
    dispatch(updateFormData(id, RUBRIQUE, "point_focal", pointFocal));
    dispatch(updateFormData(id, RUBRIQUE, "autres",      autres));
    try {
      const result = await dispatch(saveDossier());
      toast.success(result.message);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setMalade({ ...INITIAL.malade, ...savedMalade });
    setParents({ ...INITIAL.parents, ...savedParents });
    setPointFocal({ ...INITIAL.point_focal, ...savedPointFocal });
    setAutres({ ...INITIAL.autres, ...savedAutres });
  };

  if (!dossier) {
    return (
      <div className="ig-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="ig-page">
      {saving && <SaveLoader />}

      <header className="ig-header">
        <div className="ig-header-left">
          <button className="ig-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="ig-title">Dossier {dossier.numero_dossier}</h1>
            <p className="ig-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="ig-save-btn" onClick={handleSaveAll} disabled={saving}>Sauvegarder les modifications</button>
      </header>

      <div className="ig-body">
        <aside className="ig-image-panel">
          <div className="ig-image-header">
            <span className="ig-rubrique-label">Informations Générales</span>
            <span className="ig-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="ig-image-viewer">
            <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="ig-image" />
          </div>
          <div className="ig-image-nav">
            <button className="ig-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            <div className="ig-image-dots">
              {IMAGES.map((_, i) => (
                <span key={i} className={`ig-dot ${i === imgIndex ? "ig-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
              ))}
            </div>
            <button className="ig-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        <main className="ig-form-panel">
          <div className="ig-tabs">
            {TABS.map((tab) => (
              <button key={tab.id}
                className={`ig-tab ${activeTab === tab.id ? "ig-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="ig-tab-content">

            {/* ── TAB 1 : Malade ── */}
            {activeTab === "malade" && (
              <div className="ig-fields">
                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Date de Naissance <ConfidenceLabel confidence={gm("date_naissance")} /></label>
                    <input type="date" value={malade.date_naissance ?? ""}
                      onChange={(e) => changeMalade("date_naissance", e.target.value)}
                      style={{ borderColor: gm("date_naissance").color || undefined }} />
                  </div>
                  <div className="ig-field-group ig-field-group--small">
                    <label>Âge (ans) <ConfidenceLabel confidence={gm("age_ans")} /></label>
                    <input type="number" min="0" placeholder="ex : 16" value={malade.age_ans ?? ""}
                      onChange={(e) => changeMalade("age_ans", e.target.value)}
                      style={{ borderColor: gm("age_ans").color || undefined }} />
                  </div>
                  <div className="ig-field-group ig-field-group--small">
                    <label>Mois <ConfidenceLabel confidence={gm("age_mois")} /></label>
                    <input type="number" min="0" max="11" placeholder="ex : 3" value={malade.age_mois ?? ""}
                      onChange={(e) => changeMalade("age_mois", e.target.value)}
                      style={{ borderColor: gm("age_mois").color || undefined }} />
                  </div>
                </div>

                <div className="ig-field-group">
                  <label>Lieu de Naissance <ConfidenceLabel confidence={gm("lieu_naissance")} /></label>
                  <input type="text" placeholder="Ville / village de naissance" value={malade.lieu_naissance ?? ""}
                    onChange={(e) => changeMalade("lieu_naissance", e.target.value)}
                    style={{ borderColor: gm("lieu_naissance").color || undefined }} />
                </div>

                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Sexe <ConfidenceLabel confidence={gm("sexe")} /></label>
                    <select value={malade.sexe ?? ""} onChange={(e) => changeMalade("sexe", e.target.value)}
                      style={{ borderColor: gm("sexe").color || undefined }}>
                      <option value="">—</option>
                      <option value="m">Masculin</option>
                      <option value="f">Féminin</option>
                    </select>
                  </div>
                  <div className="ig-field-group">
                    <label>Race <ConfidenceLabel confidence={gm("race")} /></label>
                    <input type="text" placeholder="ex : Noire" value={malade.race ?? ""}
                      onChange={(e) => changeMalade("race", e.target.value)}
                      style={{ borderColor: gm("race").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Nationalité <ConfidenceLabel confidence={gm("nationalite")} /></label>
                    <input type="text" placeholder="ex : Ivoirienne" value={malade.nationalite ?? ""}
                      onChange={(e) => changeMalade("nationalite", e.target.value)}
                      style={{ borderColor: gm("nationalite").color || undefined }} />
                  </div>
                </div>

                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Scolarisé <ConfidenceLabel confidence={gm("scolarise")} /></label>
                    <select value={malade.scolarise ?? ""} onChange={(e) => changeMalade("scolarise", e.target.value)}
                      style={{ borderColor: gm("scolarise").color || undefined }}>
                      <option value="">—</option>
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </div>
                  <div className="ig-field-group">
                    <label>Niveau d'étude <ConfidenceLabel confidence={gm("niveau_etude")} /></label>
                    <input type="text" placeholder="ex : 6e, Terminale..." value={malade.niveau_etude ?? ""}
                      onChange={(e) => changeMalade("niveau_etude", e.target.value)}
                      style={{ borderColor: gm("niveau_etude").color || undefined }} />
                  </div>
                </div>

                <div className="ig-section-title">Résidence Habituelle</div>

                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Ville <ConfidenceLabel confidence={gm("ville")} /></label>
                    <input type="text" placeholder="Ville" value={malade.ville ?? ""}
                      onChange={(e) => changeMalade("ville", e.target.value)}
                      style={{ borderColor: gm("ville").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Commune <ConfidenceLabel confidence={gm("commune")} /></label>
                    <input type="text" placeholder="Commune" value={malade.commune ?? ""}
                      onChange={(e) => changeMalade("commune", e.target.value)}
                      style={{ borderColor: gm("commune").color || undefined }} />
                  </div>
                </div>

                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Village <ConfidenceLabel confidence={gm("village")} /></label>
                    <input type="text" placeholder="Village" value={malade.village ?? ""}
                      onChange={(e) => changeMalade("village", e.target.value)}
                      style={{ borderColor: gm("village").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Campement <ConfidenceLabel confidence={gm("campement")} /></label>
                    <input type="text" placeholder="Campement" value={malade.campement ?? ""}
                      onChange={(e) => changeMalade("campement", e.target.value)}
                      style={{ borderColor: gm("campement").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Quartier <ConfidenceLabel confidence={gm("quartier")} /></label>
                    <input type="text" placeholder="Quartier" value={malade.quartier ?? ""}
                      onChange={(e) => changeMalade("quartier", e.target.value)}
                      style={{ borderColor: gm("quartier").color || undefined }} />
                  </div>
                </div>

                <div className="ig-field-group">
                  <label>Zone d'habitation <ConfidenceLabel confidence={gm("zone_habitation")} /></label>
                  <select value={malade.zone_habitation ?? ""} onChange={(e) => changeMalade("zone_habitation", e.target.value)}
                    style={{ borderColor: gm("zone_habitation").color || undefined }}>
                    <option value="">—</option>
                    <option value="rurale">Rurale</option>
                    <option value="urbaine">Urbaine</option>
                  </select>
                </div>

                <div className="ig-field-group">
                  <label>Lieu d'hébergement à Abidjan <ConfidenceLabel confidence={gm("hebergement_abidjan")} /></label>
                  <input type="text" placeholder="Lieu d'hébergement" value={malade.hebergement_abidjan ?? ""}
                    onChange={(e) => changeMalade("hebergement_abidjan", e.target.value)}
                    style={{ borderColor: gm("hebergement_abidjan").color || undefined }} />
                </div>
              </div>
            )}

            {/* ── TAB 2 : Parents ── */}
            {activeTab === "parents" && (
              <div className="ig-fields">
                <div className="ig-field-group">
                  <label>Type de Famille <ConfidenceLabel confidence={gp("famille")} /></label>
                  <select value={parents.famille ?? ""} onChange={(e) => {
                    changeParents("famille", e.target.value);
                    if (e.target.value !== "monoparentale") changeParents("famille_monoparentale_avec", "");
                  }}
                    style={{ borderColor: gp("famille").color || undefined }}>
                    <option value="">—</option>
                    <option value="monogame">Monogame</option>
                    <option value="polygame">Polygame</option>
                    <option value="monoparentale">Monoparentale</option>
                  </select>
                </div>

                <div className="ig-field-group">
                  <label>Type de Mariage <ConfidenceLabel confidence={gp("type_mariage")} /></label>
                  <select value={parents.type_mariage ?? ""} onChange={(e) => changeParents("type_mariage", e.target.value)}
                    style={{ borderColor: gp("type_mariage").color || undefined }}>
                    <option value="">—</option>
                    <option value="civil">Civil</option>
                    <option value="religieux">Religieux</option>
                    <option value="coutumier">Coutumier</option>
                    <option value="civil_religieux">Civil + Religieux</option>
                    <option value="civil_coutumier">Civil + Coutumier</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                {parents.famille === "monoparentale" && (
                  <div className="ig-field-group">
                    <label>Famille monoparentale avec <ConfidenceLabel confidence={gp("famille_monoparentale_avec")} /></label>
                    <select value={parents.famille_monoparentale_avec ?? ""} onChange={(e) => changeParents("famille_monoparentale_avec", e.target.value)}
                      style={{ borderColor: gp("famille_monoparentale_avec").color || undefined }}>
                      <option value="">—</option>
                      <option value="pere">Le Père</option>
                      <option value="mere">La Mère</option>
                    </select>
                  </div>
                )}

                {parents.famille === "polygame" && (
                  <div className="ig-field-group">
                    <label>Nombre de femmes <ConfidenceLabel confidence={gp("nombre_femmes")} /></label>
                    <input type="number" min="1" placeholder="ex : 2" value={parents.nombre_femmes ?? ""}
                      onChange={(e) => changeParents("nombre_femmes", e.target.value)}
                      style={{ borderColor: gp("nombre_femmes").color || undefined }} />
                  </div>
                )}

                <div className="ig-sub-section">Père</div>
                <div className="ig-field-group">
                  <label>Nom / Prénoms <ConfidenceLabel confidence={gp("pere_nom")} /></label>
                  <input type="text" placeholder="Nom et prénoms du père" value={parents.pere_nom ?? ""}
                    onChange={(e) => changeParents("pere_nom", e.target.value)}
                    style={{ borderColor: gp("pere_nom").color || undefined }} />
                </div>
                <div className="ig-row">
                  <div className="ig-field-group ig-field-group--small">
                    <label>Âge (ans) <ConfidenceLabel confidence={gp("pere_age")} /></label>
                    <input type="number" min="0" placeholder="ex : 45" value={parents.pere_age ?? ""}
                      onChange={(e) => changeParents("pere_age", e.target.value)}
                      style={{ borderColor: gp("pere_age").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Ethnie <ConfidenceLabel confidence={gp("pere_ethnie")} /></label>
                    <input type="text" placeholder="Ethnie" value={parents.pere_ethnie ?? ""}
                      onChange={(e) => changeParents("pere_ethnie", e.target.value)}
                      style={{ borderColor: gp("pere_ethnie").color || undefined }} />
                  </div>
                </div>
                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Niveau d'étude <ConfidenceLabel confidence={gp("pere_niveau_etude")} /></label>
                    <select value={parents.pere_niveau_etude ?? ""} onChange={(e) => changeParents("pere_niveau_etude", e.target.value)}
                      style={{ borderColor: gp("pere_niveau_etude").color || undefined }}>
                      <option value="">—</option>
                      <option value="illetre_primaire">Illettré ou Primaire</option>
                      <option value="secondaire">Secondaire</option>
                      <option value="superieur">Supérieur</option>
                    </select>
                  </div>
                  <div className="ig-field-group">
                    <label>Religion <ConfidenceLabel confidence={gp("pere_religion")} /></label>
                    <select value={parents.pere_religion ?? ""} onChange={(e) => changeParents("pere_religion", e.target.value)}
                      style={{ borderColor: gp("pere_religion").color || undefined }}>
                      <option value="">—</option>
                      <option value="chretien">Chrétien</option>
                      <option value="musulman">Musulman</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </div>
                {parents.pere_religion === "autre" && (
                  <div className="ig-field-group">
                    <label>Précisez la religion <ConfidenceLabel confidence={gp("pere_religion_autre")} /></label>
                    <input type="text" placeholder="Précisez..." value={parents.pere_religion_autre ?? ""}
                      onChange={(e) => changeParents("pere_religion_autre", e.target.value)}
                      style={{ borderColor: gp("pere_religion_autre").color || undefined }} />
                  </div>
                )}
                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Catégorie profession <ConfidenceLabel confidence={gp("pere_profession")} /></label>
                    <select value={parents.pere_profession ?? ""} onChange={(e) => changeParents("pere_profession", e.target.value)}
                      style={{ borderColor: gp("pere_profession").color || undefined }}>
                      <option value="">—</option>
                      <option value="fonctionnaire">Fonctionnaire ordinaire</option>
                      <option value="cadre_liberal">Cadre libéral</option>
                      <option value="artisan">Artisan</option>
                      <option value="cultivateur">Cultivateur</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  {parents.pere_profession === "autre" && (
                    <div className="ig-field-group">
                      <label>Précisez <ConfidenceLabel confidence={gp("pere_profession_detail")} /></label>
                      <input type="text" placeholder="Précisez..." value={parents.pere_profession_detail ?? ""}
                        onChange={(e) => changeParents("pere_profession_detail", e.target.value)}
                        style={{ borderColor: gp("pere_profession_detail").color || undefined }} />
                    </div>
                  )}
                </div>
                <div className="ig-field-group">
                  <label>Autres contacts <ConfidenceLabel confidence={gp("pere_autres_contacts")} /></label>
                  <input type="text" placeholder="Téléphone, email..." value={parents.pere_autres_contacts ?? ""}
                    onChange={(e) => changeParents("pere_autres_contacts", e.target.value)}
                    style={{ borderColor: gp("pere_autres_contacts").color || undefined }} />
                </div>

                <div className="ig-divider" />

                <div className="ig-sub-section">Mère</div>
                <div className="ig-field-group">
                  <label>Nom / Prénom <ConfidenceLabel confidence={gp("mere_nom")} /></label>
                  <input type="text" placeholder="Nom et prénom de la mère" value={parents.mere_nom ?? ""}
                    onChange={(e) => changeParents("mere_nom", e.target.value)}
                    style={{ borderColor: gp("mere_nom").color || undefined }} />
                </div>
                <div className="ig-row">
                  <div className="ig-field-group ig-field-group--small">
                    <label>Âge (ans) <ConfidenceLabel confidence={gp("mere_age")} /></label>
                    <input type="number" min="0" placeholder="ex : 40" value={parents.mere_age ?? ""}
                      onChange={(e) => changeParents("mere_age", e.target.value)}
                      style={{ borderColor: gp("mere_age").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Ethnie <ConfidenceLabel confidence={gp("mere_ethnie")} /></label>
                    <input type="text" placeholder="Ethnie" value={parents.mere_ethnie ?? ""}
                      onChange={(e) => changeParents("mere_ethnie", e.target.value)}
                      style={{ borderColor: gp("mere_ethnie").color || undefined }} />
                  </div>
                </div>
                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Niveau d'étude <ConfidenceLabel confidence={gp("mere_niveau_etude")} /></label>
                    <select value={parents.mere_niveau_etude ?? ""} onChange={(e) => changeParents("mere_niveau_etude", e.target.value)}
                      style={{ borderColor: gp("mere_niveau_etude").color || undefined }}>
                      <option value="">—</option>
                      <option value="illetre_primaire">Illettrée ou Primaire</option>
                      <option value="secondaire">Secondaire</option>
                      <option value="superieur">Supérieur</option>
                    </select>
                  </div>
                  <div className="ig-field-group">
                    <label>Religion <ConfidenceLabel confidence={gp("mere_religion")} /></label>
                    <select value={parents.mere_religion ?? ""} onChange={(e) => changeParents("mere_religion", e.target.value)}
                      style={{ borderColor: gp("mere_religion").color || undefined }}>
                      <option value="">—</option>
                      <option value="chretienne">Chrétienne</option>
                      <option value="musulmane">Musulmane</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </div>
                {parents.mere_religion === "autre" && (
                  <div className="ig-field-group">
                    <label>Précisez la religion <ConfidenceLabel confidence={gp("mere_religion_autre")} /></label>
                    <input type="text" placeholder="Précisez..." value={parents.mere_religion_autre ?? ""}
                      onChange={(e) => changeParents("mere_religion_autre", e.target.value)}
                      style={{ borderColor: gp("mere_religion_autre").color || undefined }} />
                  </div>
                )}
                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Catégorie profession <ConfidenceLabel confidence={gp("mere_profession")} /></label>
                    <select value={parents.mere_profession ?? ""} onChange={(e) => changeParents("mere_profession", e.target.value)}
                      style={{ borderColor: gp("mere_profession").color || undefined }}>
                      <option value="">—</option>
                      <option value="fonctionnaire">Fonctionnaire ordinaire</option>
                      <option value="cadre_liberal">Cadre libéral</option>
                      <option value="artisan">Artisan</option>
                      <option value="cultivateur">Cultivateur</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  {parents.mere_profession === "autre" && (
                    <div className="ig-field-group">
                      <label>Précisez <ConfidenceLabel confidence={gp("mere_profession_detail")} /></label>
                      <input type="text" placeholder="Précisez..." value={parents.mere_profession_detail ?? ""}
                        onChange={(e) => changeParents("mere_profession_detail", e.target.value)}
                        style={{ borderColor: gp("mere_profession_detail").color || undefined }} />
                    </div>
                  )}
                </div>
                <div className="ig-field-group">
                  <label>Autres contacts <ConfidenceLabel confidence={gp("mere_autres_contacts")} /></label>
                  <input type="text" placeholder="Téléphone, email..." value={parents.mere_autres_contacts ?? ""}
                    onChange={(e) => changeParents("mere_autres_contacts", e.target.value)}
                    style={{ borderColor: gp("mere_autres_contacts").color || undefined }} />
                </div>
              </div>
            )}

            {/* ── TAB 3 : Point Focal ── */}
            {activeTab === "point_focal" && (
              <div className="ig-fields">
                <div className="ig-field-group">
                  <label>Nom / Prénom <ConfidenceLabel confidence={gpf("nom")} /></label>
                  <input type="text" placeholder="Nom et prénom" value={pointFocal.nom ?? ""}
                    onChange={(e) => changePointFocal("nom", e.target.value)}
                    style={{ borderColor: gpf("nom").color || undefined }} />
                </div>
                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>Lien de parenté <ConfidenceLabel confidence={gpf("lien_parente")} /></label>
                    <input type="text" placeholder="ex : Grand-Frère, Oncle..." value={pointFocal.lien_parente ?? ""}
                      onChange={(e) => changePointFocal("lien_parente", e.target.value)}
                      style={{ borderColor: gpf("lien_parente").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Profession <ConfidenceLabel confidence={gpf("profession")} /></label>
                    <input type="text" placeholder="Profession" value={pointFocal.profession ?? ""}
                      onChange={(e) => changePointFocal("profession", e.target.value)}
                      style={{ borderColor: gpf("profession").color || undefined }} />
                  </div>
                </div>
                <div className="ig-field-group">
                  <label>Lieu d'habitation <ConfidenceLabel confidence={gpf("lieu_habitation")} /></label>
                  <input type="text" placeholder="Lieu d'habitation" value={pointFocal.lieu_habitation ?? ""}
                    onChange={(e) => changePointFocal("lieu_habitation", e.target.value)}
                    style={{ borderColor: gpf("lieu_habitation").color || undefined }} />
                </div>
                <div className="ig-row">
                  <div className="ig-field-group">
                    <label>E-mail <ConfidenceLabel confidence={gpf("email")} /></label>
                    <input type="email" placeholder="email@exemple.com" value={pointFocal.email ?? ""}
                      onChange={(e) => changePointFocal("email", e.target.value)}
                      style={{ borderColor: gpf("email").color || undefined }} />
                  </div>
                  <div className="ig-field-group">
                    <label>Mobile <ConfidenceLabel confidence={gpf("mobile")} /></label>
                    <input type="tel" placeholder="+225 00 00 00 00 00" value={pointFocal.mobile ?? ""}
                      onChange={(e) => changePointFocal("mobile", e.target.value)}
                      style={{ borderColor: gpf("mobile").color || undefined }} />
                  </div>
                </div>
              </div>
            )}

            {/* ── TAB 4 : Autres Contacts ── */}
            {activeTab === "autres" && (
              <div className="ig-fields">
                <div className="ig-field-group">
                  <label>Nom / Prénom <ConfidenceLabel confidence={ga("nom")} /></label>
                  <input type="text" placeholder="Nom et prénom" value={autres.nom ?? ""}
                    onChange={(e) => changeAutres("nom", e.target.value)}
                    style={{ borderColor: ga("nom").color || undefined }} />
                </div>
                <div className="ig-field-group">
                  <label>Lien de parenté <ConfidenceLabel confidence={ga("lien_parente")} /></label>
                  <input type="text" placeholder="ex : Cousin, Voisin..." value={autres.lien_parente ?? ""}
                    onChange={(e) => changeAutres("lien_parente", e.target.value)}
                    style={{ borderColor: ga("lien_parente").color || undefined }} />
                </div>
                <div className="ig-field-group">
                  <label>Contacts <ConfidenceLabel confidence={ga("contacts")} /></label>
                  <input type="text" placeholder="Téléphone, email..." value={autres.contacts ?? ""}
                    onChange={(e) => changeAutres("contacts", e.target.value)}
                    style={{ borderColor: ga("contacts").color || undefined }} />
                </div>
              </div>
            )}
          </div>

          {/* <div className="ig-form-actions">
            <button className="ig-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="ig-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}


<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />        </main>
      </div>
    </div>
  );
};

export default InfosGenerales;